/**
 * @tensilelabs/auth-client — Cross-Project PKI & Passkey PRF Cryptography
 *
 * Uses WebCrypto (SubtleCrypto) so it runs identically in browsers, Cloudflare Workers,
 * and Node.js 20+ with zero native dependencies.
 */

export type AccountRole = "owner" | "admin" | "run_only";

/**
 * Canonical vault categories — must match `firestore.rules: isValidAppSecret` and the worker's
 * `EncryptedSecretRecord`. Previously the SDK used a different spelling
 * (`deep_seek_api_key` / `dynamic_agent_field`), which made the sealed envelope disagree with the
 * schema it was stored under.
 */
export type SecretCategory =
  | "deepseek_api_key"
  | "payment_card"
  | "identity_info"
  | "agent_dynamic"
  | "custom";

export interface WrappedDeviceMasterKey {
  device_id: string;
  device_name: string;
  credential_id_b64: string;
  prf_salt_b64: string;
  nonce_b64: string;
  wrapped_seed_b64: string;
  created_at: string;
}

export interface EncryptedSecretEnvelope {
  secret_id: string;
  /** Presentation metadata; absent on envelopes produced server-side by `crypto-pki`. */
  label?: string;
  account_id: string;
  app_id: string;
  category: SecretCategory;
  is_agent_dynamic: boolean;
  ephemeral_public_key: string;
  nonce: string;
  ciphertext: string;
  updated_at: string;
}

export interface EncryptedSessionEnvelope {
  account_id: string;
  app_id: string;
  domain: string;
  /** Presentation metadata; absent on envelopes produced server-side by `crypto-pki`. */
  cookie_count?: number;
  ephemeral_public_key: string;
  nonce: string;
  ciphertext: string;
  updated_at: string;
}

export interface DevicePairingPackage {
  pairing_id: string;
  account_id: string;
  salt_b64: string;
  nonce_b64: string;
  wrapped_seed_b64: string;
  expires_at: string;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/**
 * Byte buffer backed by a plain `ArrayBuffer`. WebCrypto's `BufferSource` does not accept
 * `Uint8Array<ArrayBufferLike>` (the TS 5.7+ default), so all byte material that crosses
 * the WebCrypto boundary is typed as `Bytes`.
 */
export type Bytes = Uint8Array<ArrayBuffer>;

export function bytesToBase64(bytes: Bytes): string {
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Decodes base64 **and** base64url, padded or unpadded.
 *
 * WebAuthn's JSON surface (challenges, credential ids) is base64url without padding, and the edge
 * worker returns `challenge_b64` as `toBase64Url(...)`. A strict `atob` rejects the URL-safe
 * alphabet (`-`, `_`) and the missing padding, which is why a plain-base64 decode used to throw on
 * roughly three out of four random 32-byte challenges and silently collapse into the software
 * passkey fallback.
 */
export function base64ToBytes(b64: string): Bytes {
  const normalized = b64.replace(/-/g, "+").replace(/_/g, "/").replace(/=+$/, "");
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
  const binary = atob(padded);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

/**
 * Generates a fresh 32-byte Master Identity Seed for a Tensile Labs user/account.
 */
export function generateMasterIdentitySeed(): Bytes {
  const seed = new Uint8Array(32);
  crypto.getRandomValues(seed);
  return seed;
}

/**
 * Computes a deterministic 16-byte hex fingerprint for displaying the user's PKI identity.
 */
export async function computeIdentityFingerprint(masterSeed: Bytes): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", masterSeed);
  const bytes = new Uint8Array(digest).slice(0, 8);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join(":");
}

async function deriveHkdfAesKey(
  ikm: Bytes,
  salt: Bytes,
  info: string
): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey("raw", ikm, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt,
      info: encoder.encode(info),
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
}

/**
 * Wraps the 32-byte Master Identity Seed under an authorized device's WebAuthn Passkey PRF output.
 */
export async function wrapMasterKeyForDevice(params: {
  masterSeed: Bytes;
  deviceId: string;
  deviceName: string;
  credentialIdB64: string;
  passkeyPrfOutput: Bytes;
  prfSalt?: Bytes;
}): Promise<WrappedDeviceMasterKey> {
  if (params.passkeyPrfOutput.byteLength < 16) {
    throw new Error("Passkey PRF output must be at least 16 bytes");
  }
  const salt = params.prfSalt ?? crypto.getRandomValues(new Uint8Array(16));
  const kek = await deriveHkdfAesKey(
    params.passkeyPrfOutput,
    salt,
    "tensilelabs:passkey-prf-kek:v1"
  );
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const wrapped = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce },
    kek,
    params.masterSeed
  );

  return {
    device_id: params.deviceId,
    device_name: params.deviceName,
    credential_id_b64: params.credentialIdB64,
    prf_salt_b64: bytesToBase64(salt),
    nonce_b64: bytesToBase64(nonce),
    wrapped_seed_b64: bytesToBase64(new Uint8Array(wrapped)),
    created_at: new Date().toISOString(),
  };
}

/**
 * Unwraps the 32-byte Master Identity Seed on an authorized device using its Passkey PRF output.
 */
export async function unwrapMasterKeyFromDevice(
  envelope: WrappedDeviceMasterKey,
  passkeyPrfOutput: Bytes
): Promise<Bytes> {
  const salt = base64ToBytes(envelope.prf_salt_b64);
  const nonce = base64ToBytes(envelope.nonce_b64);
  const ciphertext = base64ToBytes(envelope.wrapped_seed_b64);
  const kek = await deriveHkdfAesKey(passkeyPrfOutput, salt, "tensilelabs:passkey-prf-kek:v1");
  const plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv: nonce }, kek, ciphertext);
  return new Uint8Array(plaintext);
}

/**
 * Derives the tenant's app-scoped **X25519 keypair** from the master seed via
 * HKDF-SHA256 (`info = "tensilelabs:vault:<appId>:v1"`), isolating TaskJinn
 * (`appId = "taskjinn"`), the shared org vault, and future tools from each other.
 *
 * The public key is what envelopes are sealed to; the secret key never leaves the client
 * except when the user explicitly unlocks one operation (Phase 3 session restore, Phase 4
 * key grants). This is the only construction the Rust `SecretVault` understands.
 */
export async function deriveAppScopedKeypair(
  masterSeed: Bytes,
  appId: string
): Promise<{ privateKey: CryptoKey; publicKey: CryptoKey; publicKeyB64: string; publicKeyFingerprintB64: string }> {
  const normalizedApp = appId.trim();
  if (!normalizedApp) {
    throw new Error("appId cannot be empty");
  }
  const seed = await deriveHkdfBytes(
    masterSeed,
    encoder.encode("tensilelabs:master-salt:v1"),
    `tensilelabs:vault:${normalizedApp}:v1`
  );
  const privateKey = await importX25519Private(seed);
  const { key: publicKey, b64: publicKeyB64 } = await x25519PublicKey(privateKey);
  const pubRaw = base64ToBytes(publicKeyB64);
  const fingerprint = new Uint8Array(await crypto.subtle.digest("SHA-256", pubRaw));
  return {
    privateKey,
    publicKey,
    publicKeyB64,
    publicKeyFingerprintB64: bytesToBase64(fingerprint),
  };
}

/**
 * Encrypts a structured secret with tenant/app/secretId AAD binding.
 *
 * Sealed to the tenant's app-scoped public key with X25519 ECIES + AES-256-GCM. The
 * `ephemeral_public_key` field is a real ephemeral public key (not a fingerprint), which is
 * what lets the Rust `SecretVault` open the envelope when the user unlocks the app key.
 */
export async function encryptAppSecret(params: {
  masterSeed: Bytes;
  accountId: string;
  appId: string;
  secretId: string;
  label: string;
  category: SecretCategory;
  isAgentDynamic: boolean;
  plaintext: string;
}): Promise<EncryptedSecretEnvelope> {
  const { publicKeyB64 } = await deriveAppScopedKeypair(params.masterSeed, params.appId);
  const aad = `tensilelabs:secret:v1:${params.accountId}:${params.appId}:${params.secretId}`;
  const sealed = await sealToRecipient(publicKeyB64, aad, encoder.encode(params.plaintext));

  return {
    secret_id: params.secretId,
    label: params.label,
    account_id: params.accountId,
    app_id: params.appId,
    category: params.category,
    is_agent_dynamic: params.isAgentDynamic,
    ephemeral_public_key: sealed.ephemeralPublicKeyB64,
    nonce: sealed.nonceB64,
    ciphertext: sealed.ciphertextB64,
    updated_at: new Date().toISOString(),
  };
}

/** Public key (base64) matching an unlocked app-scoped secret key. */
export async function appScopedPublicKeyFromSecretKeyB64(
  appScopedSecretKeyB64: string
): Promise<string> {
  const raw = base64ToBytes(appScopedSecretKeyB64);
  if (raw.length !== 32) {
    throw new Error("app-scoped secret key must be 32 raw bytes");
  }
  const privateKey = await importX25519Private(raw);
  const { b64 } = await x25519PublicKey(privateKey);
  return b64;
}

/**
 * Encrypts a structured secret with an already-unlocked app-scoped key (the `useAppVault`
 * path). Same envelope and AAD as {@link encryptAppSecret}, but no master seed is needed:
 * the app-scoped private key re-derives the public key it seals to.
 */
export async function encryptAppSecretWithAppKey(params: {
  appScopedSecretKeyB64: string;
  accountId: string;
  appId: string;
  secretId: string;
  label: string;
  category: SecretCategory;
  isAgentDynamic: boolean;
  plaintext: string;
}): Promise<EncryptedSecretEnvelope> {
  const raw = base64ToBytes(params.appScopedSecretKeyB64);
  if (raw.length !== 32) {
    throw new Error("app-scoped secret key must be 32 raw bytes");
  }
  const privateKey = await importX25519Private(raw);
  const { b64: publicKeyB64 } = await x25519PublicKey(privateKey);
  const aad = `tensilelabs:secret:v1:${params.accountId}:${params.appId}:${params.secretId}`;
  const sealed = await sealToRecipient(publicKeyB64, aad, encoder.encode(params.plaintext));
  return {
    secret_id: params.secretId,
    label: params.label,
    account_id: params.accountId,
    app_id: params.appId,
    category: params.category,
    is_agent_dynamic: params.isAgentDynamic,
    ephemeral_public_key: sealed.ephemeralPublicKeyB64,
    nonce: sealed.nonceB64,
    ciphertext: sealed.ciphertextB64,
    updated_at: new Date().toISOString(),
  };
}

/**
 * Decrypts a secret envelope with the already-unlocked app-scoped key (`useAppVault`).
 * Counterpart to {@link encryptAppSecretWithAppKey}; no Master seed is required.
 */
export async function decryptAppSecretWithAppKey(params: {
  appScopedSecretKeyB64: string;
  envelope: EncryptedSecretEnvelope;
}): Promise<string> {
  const raw = base64ToBytes(params.appScopedSecretKeyB64);
  if (raw.length !== 32) {
    throw new Error("app-scoped secret key must be 32 raw bytes");
  }
  const privateKey = await importX25519Private(raw);
  const { b64: publicKeyB64 } = await x25519PublicKey(privateKey);
  const { envelope } = params;
  const aad = `tensilelabs:secret:v1:${envelope.account_id}:${envelope.app_id}:${envelope.secret_id}`;
  return openFromRecipient(privateKey, publicKeyB64, aad, {
    ephemeralPublicKeyB64: envelope.ephemeral_public_key,
    nonceB64: envelope.nonce,
    ciphertextB64: envelope.ciphertext,
  });
}

/**
 * Decrypts a structured secret envelope produced by either this client or the Rust vault.
 */
export async function decryptAppSecret(
  masterSeed: Bytes,
  envelope: EncryptedSecretEnvelope
): Promise<string> {
  const { privateKey, publicKeyB64 } = await deriveAppScopedKeypair(
    masterSeed,
    envelope.app_id
  );
  const aad = `tensilelabs:secret:v1:${envelope.account_id}:${envelope.app_id}:${envelope.secret_id}`;
  return openFromRecipient(privateKey, publicKeyB64, aad, {
    ephemeralPublicKeyB64: envelope.ephemeral_public_key,
    nonceB64: envelope.nonce,
    ciphertextB64: envelope.ciphertext,
  });
}

/**
 * Encrypts a CloakBrowser / Playwright `storageState` cookie bundle for a domain.
 */
export async function encryptBrowserSessionState(params: {
  masterSeed: Bytes;
  accountId: string;
  appId: string;
  domain: string;
  storageStateJson: string;
  cookieCount: number;
}): Promise<EncryptedSessionEnvelope> {
  const { publicKeyB64 } = await deriveAppScopedKeypair(params.masterSeed, params.appId);
  const aad = `tensilelabs:session:v1:${params.accountId}:${params.appId}:${params.domain}`;
  const sealed = await sealToRecipient(
    publicKeyB64,
    aad,
    encoder.encode(params.storageStateJson)
  );

  return {
    account_id: params.accountId,
    app_id: params.appId,
    domain: params.domain,
    cookie_count: params.cookieCount,
    ephemeral_public_key: sealed.ephemeralPublicKeyB64,
    nonce: sealed.nonceB64,
    ciphertext: sealed.ciphertextB64,
    updated_at: new Date().toISOString(),
  };
}

/**
 * Decrypts a CloakBrowser / Playwright `storageState` cookie bundle.
 */
export async function decryptBrowserSessionState(
  masterSeed: Bytes,
  envelope: EncryptedSessionEnvelope
): Promise<string> {
  const { privateKey, publicKeyB64 } = await deriveAppScopedKeypair(
    masterSeed,
    envelope.app_id
  );
  const aad = `tensilelabs:session:v1:${envelope.account_id}:${envelope.app_id}:${envelope.domain}`;
  return openFromRecipient(privateKey, publicKeyB64, aad, {
    ephemeralPublicKeyB64: envelope.ephemeral_public_key,
    nonceB64: envelope.nonce,
    ciphertextB64: envelope.ciphertext,
  });
}


/**
 * Exports the app-scoped X25519 **secret** key (base64) so the user can unlock one
 * server-side operation that needs to open a vault envelope — Phase 3 `storageState`
 * restore (`StartWorkerSessionRequest.authorize_decrypt_secret_key_b64`) and Phase 4 BYOK.
 *
 * Call this only for the operation being unlocked: the secret key opens every envelope for
 * that app, so it should never be stored or reused across operations.
 */
export async function exportAppScopedSecretKeyB64(
  masterSeed: Bytes,
  appId: string
): Promise<string> {
  const { privateKey } = await deriveAppScopedKeypair(masterSeed, appId);
  try {
    const raw = new Uint8Array(await crypto.subtle.exportKey("raw", privateKey));
    if (raw.length === 32) {
      return bytesToBase64(raw);
    }
  } catch {
    // Fall through to PKCS#8, which some browsers expose instead.
  }
  const pkcs8 = new Uint8Array(await crypto.subtle.exportKey("pkcs8", privateKey));
  return bytesToBase64(pkcs8.slice(pkcs8.length - 32));
}

// -----------------------------------------------------------------------------------
// X25519 ECIES primitives (must match `crates/crypto-pki` and `keygrant.rs`)
// -----------------------------------------------------------------------------------

/** `base64ToBytes` already accepts the URL-safe alphabet; kept as a named alias for call sites. */
function b64UrlToBytes(b64url: string): Bytes {
  return base64ToBytes(b64url);
}

async function deriveHkdfBytes(
  ikm: Bytes,
  salt: Bytes,
  info: string
): Promise<Bytes> {
  const base = await crypto.subtle.importKey("raw", ikm as BufferSource, "HKDF", false, [
    "deriveBits",
  ]);
  const bits = await crypto.subtle.deriveBits(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: salt as BufferSource,
      info: encoder.encode(info) as BufferSource,
    },
    base,
    256
  );
  return new Uint8Array(bits);
}

async function importX25519Private(seed: Bytes): Promise<CryptoKey> {
  try {
    return await crypto.subtle.importKey(
      "raw",
      seed as BufferSource,
      { name: "X25519" },
      true,
      ["deriveBits"]
    );
  } catch {
    // Browsers that only accept PKCS#8 for X25519 private keys.
    const pkcs8 = new Uint8Array(16 + 32);
    pkcs8.set(
      [0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06, 0x03, 0x2b, 0x65, 0x6e, 0x04, 0x22, 0x04, 0x20],
      0
    );
    pkcs8.set(seed, 16);
    return crypto.subtle.importKey(
      "pkcs8",
      pkcs8 as BufferSource,
      { name: "X25519" },
      true,
      ["deriveBits"]
    );
  }
}

async function x25519PublicKey(
  privateKey: CryptoKey
): Promise<{ key: CryptoKey; b64: string }> {
  const jwk = await crypto.subtle.exportKey("jwk", privateKey);
  if (!jwk.x) {
    throw new Error("X25519 JWK export did not include the public component");
  }
  const raw = b64UrlToBytes(jwk.x);
  const key = await crypto.subtle.importKey(
    "raw",
    raw as BufferSource,
    { name: "X25519" },
    true,
    []
  );
  return { key, b64: bytesToBase64(raw) };
}

async function importX25519Public(b64: string): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    "raw",
    base64ToBytes(b64) as BufferSource,
    { name: "X25519" },
    true,
    []
  );
}

async function deriveEnvelopeKey(
  sharedBits: ArrayBuffer,
  ephemeralPublicKeyB64: string,
  recipientPublicKeyB64: string,
  aad: string
): Promise<CryptoKey> {
  const salt = new Uint8Array(
    base64ToBytes(ephemeralPublicKeyB64).length +
      base64ToBytes(recipientPublicKeyB64).length
  );
  salt.set(base64ToBytes(ephemeralPublicKeyB64), 0);
  salt.set(base64ToBytes(recipientPublicKeyB64), base64ToBytes(ephemeralPublicKeyB64).length);

  const base = await crypto.subtle.importKey("raw", sharedBits, "HKDF", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    {
      name: "HKDF",
      hash: "SHA-256",
      salt: salt as BufferSource,
      info: encoder.encode(aad) as BufferSource,
    },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

async function sealToRecipient(
  recipientPublicKeyB64: string,
  aad: string,
  plaintext: Bytes
): Promise<{ ephemeralPublicKeyB64: string; nonceB64: string; ciphertextB64: string }> {
  const recipientPub = await importX25519Public(recipientPublicKeyB64);
  const ephemeral = (await crypto.subtle.generateKey({ name: "X25519" }, true, [
    "deriveBits",
  ])) as CryptoKeyPair;
  const ephemeralPublicKeyB64 = bytesToBase64(
    new Uint8Array(await crypto.subtle.exportKey("raw", ephemeral.publicKey))
  );
  const shared = await crypto.subtle.deriveBits(
    { name: "X25519", public: recipientPub },
    ephemeral.privateKey,
    256
  );
  const key = await deriveEnvelopeKey(shared, ephemeralPublicKeyB64, recipientPublicKeyB64, aad);
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce as BufferSource, additionalData: encoder.encode(aad) as BufferSource },
    key,
    plaintext as BufferSource
  );
  return {
    ephemeralPublicKeyB64,
    nonceB64: bytesToBase64(nonce),
    ciphertextB64: bytesToBase64(new Uint8Array(ciphertext)),
  };
}

async function openFromRecipient(
  recipientPrivateKey: CryptoKey,
  recipientPublicKeyB64: string,
  aad: string,
  sealed: { ephemeralPublicKeyB64: string; nonceB64: string; ciphertextB64: string }
): Promise<string> {
  const ephemeralPub = await importX25519Public(sealed.ephemeralPublicKeyB64);
  const shared = await crypto.subtle.deriveBits(
    { name: "X25519", public: ephemeralPub },
    recipientPrivateKey,
    256
  );
  const key = await deriveEnvelopeKey(
    shared,
    sealed.ephemeralPublicKeyB64,
    recipientPublicKeyB64,
    aad
  );
  const plaintext = await crypto.subtle.decrypt(
    {
      name: "AES-GCM",
      iv: base64ToBytes(sealed.nonceB64) as BufferSource,
      additionalData: encoder.encode(aad) as BufferSource,
    },
    key,
    base64ToBytes(sealed.ciphertextB64) as BufferSource
  );
  return decoder.decode(plaintext);
}

/**
 * Creates a one-time pairing package to authorize a second device (e.g., phone or tablet)
 * using a 6-character alphanumeric pairing code.
 */
export async function createDevicePairingPackage(params: {
  masterSeed: Bytes;
  accountId: string;
  pairingCode: string;
}): Promise<DevicePairingPackage> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const nonce = crypto.getRandomValues(new Uint8Array(12));
  const kek = await deriveHkdfAesKey(
    encoder.encode(params.pairingCode.trim().toUpperCase()),
    salt,
    `tensilelabs:device-pair:v1:${params.accountId}`
  );
  const wrapped = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce },
    kek,
    params.masterSeed
  );

  return {
    pairing_id: crypto.randomUUID(),
    account_id: params.accountId,
    salt_b64: bytesToBase64(salt),
    nonce_b64: bytesToBase64(nonce),
    wrapped_seed_b64: bytesToBase64(new Uint8Array(wrapped)),
    expires_at: new Date(Date.now() + 10 * 60 * 1000).toISOString(),
  };
}

/**
 * Unwraps a `DevicePairingPackage` on the new device and immediately re-wraps the
 * Master Identity Seed with the new device's own Passkey PRF output.
 */
export async function acceptDevicePairingPackage(params: {
  pkg: DevicePairingPackage;
  pairingCode: string;
  newDeviceId: string;
  newDeviceName: string;
  newCredentialIdB64: string;
  newPasskeyPrfOutput: Bytes;
}): Promise<{ masterSeed: Bytes; deviceEnvelope: WrappedDeviceMasterKey }> {
  const salt = base64ToBytes(params.pkg.salt_b64);
  const nonce = base64ToBytes(params.pkg.nonce_b64);
  const ciphertext = base64ToBytes(params.pkg.wrapped_seed_b64);
  const kek = await deriveHkdfAesKey(
    encoder.encode(params.pairingCode.trim().toUpperCase()),
    salt,
    `tensilelabs:device-pair:v1:${params.pkg.account_id}`
  );
  const rawSeed = await crypto.subtle.decrypt({ name: "AES-GCM", iv: nonce }, kek, ciphertext);
  const masterSeed = new Uint8Array(rawSeed);
  const deviceEnvelope = await wrapMasterKeyForDevice({
    masterSeed,
    deviceId: params.newDeviceId,
    deviceName: params.newDeviceName,
    credentialIdB64: params.newCredentialIdB64,
    passkeyPrfOutput: params.newPasskeyPrfOutput,
  });
  return { masterSeed, deviceEnvelope };
}
